import React, { useState, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { 
  FaStickyNote, FaPlus, FaSearch, FaEdit, FaTrash, FaSave, 
  FaTimes, FaTag, FaUser, FaClock, FaFilter 
} from 'react-icons/fa';
import { ENDPOINT } from '../api/auth';
import axios from 'axios';
import './Notes.css';
import 'react-toastify/dist/ReactToastify.css';

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20
  });

  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    priority: 'medium',
    tags: ''
  });

  const [editNote, setEditNote] = useState({
    id: null,
    title: '',
    content: '',
    priority: 'medium',
    tags: ''
  });

  // Fetch notes from backend
  const fetchNotes = useCallback(async (page = 1, search = '', priority = '') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        action: 'get_notes',
        page: page.toString(),
        limit: '20'
      });

      if (search) params.append('search', search);
      if (priority) params.append('priority', priority);

      const response = await axios.get(`${ENDPOINT}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setNotes(response.data.notes || []);
        setPagination(response.data.pagination || {});
      } else {
        throw new Error(response.data.message || 'Failed to fetch notes');
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error(error.response?.data?.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notes on component mount
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Handle search and filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchNotes(1, searchTerm, priorityFilter);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, priorityFilter, fetchNotes]);

  // Create new note
  const handleCreateNote = async () => {
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {
          action: 'create_note',
          title: newNote.title,
          content: newNote.content,
          priority: newNote.priority,
          tags: newNote.tags
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Note created successfully');
        setNewNote({ title: '', content: '', priority: 'medium', tags: '' });
        setIsCreating(false);
        fetchNotes(pagination.current_page, searchTerm, priorityFilter);
      } else {
        throw new Error(response.data.message || 'Failed to create note');
      }
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error(error.response?.data?.message || 'Failed to create note');
    }
  };

  // Update note
  const handleUpdateNote = async () => {
    if (!editNote.title.trim() || !editNote.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {
          action: 'update_note',
          note_id: editNote.id,
          title: editNote.title,
          content: editNote.content,
          priority: editNote.priority,
          tags: editNote.tags
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Note updated successfully');
        setEditingId(null);
        setEditNote({ id: null, title: '', content: '', priority: 'medium', tags: '' });
        fetchNotes(pagination.current_page, searchTerm, priorityFilter);
      } else {
        throw new Error(response.data.message || 'Failed to update note');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error(error.response?.data?.message || 'Failed to update note');
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId, noteTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${noteTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        ENDPOINT,
        {
          action: 'delete_note',
          note_id: noteId
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Note deleted successfully');
        fetchNotes(pagination.current_page, searchTerm, priorityFilter);
      } else {
        throw new Error(response.data.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(error.response?.data?.message || 'Failed to delete note');
    }
  };

  // Start editing a note
  const startEditing = (note) => {
    setEditingId(note.id);
    setEditNote({
      id: note.id,
      title: note.title,
      content: note.content,
      priority: note.priority,
      tags: note.tags || ''
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditNote({ id: null, title: '', content: '', priority: 'medium', tags: '' });
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchNotes(newPage, searchTerm, priorityFilter);
    }
  };

  return (
    <div className="notes-container">
      <div className="notes-header">
        <h1><FaStickyNote /> Notes</h1>
        <p>Create and manage your personal notes</p>
      </div>

      {/* Controls */}
      <div className="notes-controls">
        <div className="search-filters">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-box">
            <FaFilter className="filter-icon" />
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Priorities</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => setIsCreating(true)}
        >
          <FaPlus /> New Note
        </button>
      </div>

      {/* Create Note Form */}
      {isCreating && (
        <div className="note-form">
          <h3>Create New Note</h3>
          <div className="form-group">
            <input
              type="text"
              placeholder="Note title..."
              value={newNote.title}
              onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <textarea
              placeholder="Write your note content here..."
              value={newNote.content}
              onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
              className="form-control note-textarea"
              rows="6"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Priority</label>
              <select
                value={newNote.priority}
                onChange={(e) => setNewNote(prev => ({ ...prev, priority: e.target.value }))}
                className="form-control"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                placeholder="tag1, tag2, tag3"
                value={newNote.tags}
                onChange={(e) => setNewNote(prev => ({ ...prev, tags: e.target.value }))}
                className="form-control"
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleCreateNote}>
              <FaSave /> Save Note
            </button>
            <button className="btn btn-secondary" onClick={() => setIsCreating(false)}>
              <FaTimes /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="notes-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="empty-state">
            <FaStickyNote className="empty-icon" />
            <h3>No notes found</h3>
            <p>Create your first note to get started!</p>
          </div>
        ) : (
          notes.map(note => (
            <div key={note.id} className="note-card">
              {editingId === note.id ? (
                // Edit mode
                <div className="note-edit-form">
                  <input
                    type="text"
                    value={editNote.title}
                    onChange={(e) => setEditNote(prev => ({ ...prev, title: e.target.value }))}
                    className="form-control note-title-edit"
                  />
                  <textarea
                    value={editNote.content}
                    onChange={(e) => setEditNote(prev => ({ ...prev, content: e.target.value }))}
                    className="form-control note-content-edit"
                    rows="6"
                  />
                  <div className="edit-controls">
                    <select
                      value={editNote.priority}
                      onChange={(e) => setEditNote(prev => ({ ...prev, priority: e.target.value }))}
                      className="form-control priority-select"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Tags (comma separated)"
                      value={editNote.tags}
                      onChange={(e) => setEditNote(prev => ({ ...prev, tags: e.target.value }))}
                      className="form-control tags-input"
                    />
                  </div>
                  <div className="edit-actions">
                    <button className="btn btn-primary btn-sm" onClick={handleUpdateNote}>
                      <FaSave /> Save
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={cancelEditing}>
                      <FaTimes /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div className="note-content">
                  <div className="note-header">
                    <h3 className="note-title">{note.title}</h3>
                    <div className="note-actions">
                      <button
                        className="btn-icon"
                        onClick={() => startEditing(note)}
                        title="Edit note"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteNote(note.id, note.title)}
                        title="Delete note"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  <div className="note-body">
                    <p className="note-text">{note.content}</p>
                  </div>
                  
                  <div className="note-footer">
                    <div className="note-meta">
                      <span 
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(note.priority) }}
                      >
                        {note.priority}
                      </span>
                      
                      {note.tags && (
                        <div className="note-tags">
                          <FaTag className="tag-icon" />
                          {note.tags.split(',').map((tag, index) => (
                            <span key={index} className="tag">
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="note-info">
                      <div className="author-info">
                        <FaUser className="info-icon" />
                        <span>{note.author_name}</span>
                      </div>
                      <div className="date-info">
                        <FaClock className="info-icon" />
                        <span>{formatDate(note.created_at)}</span>
                        {note.updated_at !== note.created_at && (
                          <span className="updated-text">(Updated: {formatDate(note.updated_at)})</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page <= 1}
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {pagination.current_page} of {pagination.total_pages} 
            ({pagination.total_count} total notes)
          </span>
          
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page >= pagination.total_pages}
          >
            Next
          </button>
        </div>
      )}

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Notes;
